import React, { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { motion } from 'motion/react';
import {
  ChevronDown, Search, MessageCircle, ExternalLink, HelpCircle,
  Zap, CreditCard, BarChart2, Shield, Settings
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';

interface FAQPageProps {
  formData?: any;
}

const DISCORD_URL = 'https://discord.gg/nshaJqNA4X';

const faqCategories = [
  { id: 'getting-started', icon: Zap,        label: 'Getting Started',    color: 'text-amber-400',  bg: 'bg-amber-500/10',  border: 'border-amber-500/20'  },
  { id: 'sync-api',        icon: Settings,    label: 'Sync & API',         color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20'   },
  { id: 'trading',         icon: BarChart2,   label: 'Trading & Journal',  color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/20'  },
  { id: 'billing',         icon: CreditCard,  label: 'Plans & Billing',    color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  { id: 'security',        icon: Shield,      label: 'Security & Privacy', color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/20'    },
];

const faqs = [
  { category: 'getting-started', q: 'What is CatatCrypto?',                                    a: 'CatatCrypto is a premium trading journal for crypto traders. This platform helps you record, analyze, and improve your trading performance — both automatically via exchange synchronization and manual entry.' },
  { category: 'getting-started', q: 'How do I start using CatatCrypto?',                       a: 'Register an account, then connect your exchange via API Key (for auto sync) or input manual trades directly. The dashboard will automatically display your trading stats as soon as the data is loaded.' },
  { category: 'getting-started', q: 'Which exchanges are supported?',                        a: 'Currently, CatatCrypto supports: Binance, Indodax, OKX, MEXC, Bitget, Indodax, and Tokocrypto. New exchange support is constantly being added — watch the Changelog page for the latest updates.' },
  { category: 'getting-started', q: 'Do I have to connect a CEX to use CatatCrypto?',         a: 'No. You can input trades manually via the "Add Trade Manual" feature without needing an API Key. CEX synchronization is optional for convenience.' },
  { category: 'sync-api',        q: 'How do I connect my CEX API Key?',                      a: 'Go to Settings → CEX Accounts → Add Account. Enter your API Key and API Secret. Make sure the API Key only has "Read Only" permissions — never give withdrawal permissions to any platform.' },
  { category: 'sync-api',        q: 'My trading data does not appear after syncing. Why?',   a: 'Possible reasons: (1) The API Key does not have "Trade History Read" permission, (2) Synchronization takes a few minutes for exchanges with large histories, (3) Ensure the API Key is active and not expired on your exchange. Try clicking Re-sync on the Trades page.' },
  { category: 'sync-api',        q: 'Can CatatCrypto trade on my behalf?',                   a: 'No. CatatCrypto only reads your trading history data (read-only). We never ask for trading, withdrawal, or transfer permissions. Your asset security is 100% guaranteed.' },
  { category: 'sync-api',        q: 'How often is trading data synced automatically?',        a: 'You can trigger sync anytime using the Re-sync button on the Trades page. For Pro accounts and above, sync is more frequent and supports longer historical data.' },
  { category: 'trading',         q: 'How do I input manual trades?',                          a: 'Click the "+" or "Add Trade" button in the Sidebar. The wizard will guide you through entering trade details: pair, date, entry/exit price, quantity, leverage, and strategy notes. Both SPOT and FUTURES modes are available.' },
  { category: 'trading',         q: 'What is the difference between "per Token" vs "per USDT" input?', a: '"per Token" mode: you enter the token quantity directly (e.g., 0.5 BTC). "per USDT" mode: you enter the USDT value used (e.g., $500), and the system automatically calculates the token quantity based on the entry price.' },
  { category: 'trading',         q: 'Why does my trade Entry Price show as N/A?',               a: 'This can happen for older trades saved in legacy formats. Try Re-syncing your account or re-adding the trade manually. New trades entered after the update automatically store the entry price correctly.' },
  { category: 'trading',         q: 'Are the notes I write saved?',                           a: 'Yes. All remarks/notes you write on the trade detail page are saved to our database and can be accessed anytime. Make sure to click the "Save Notes" button after writing.' },
  { category: 'billing',         q: 'What is the difference between Free vs Pro plans?',      a: 'The Free plan is enough to start with basic journal features and 1 CEX account. The Pro plan provides unlimited trade history, multiple CEX accounts, AI analysis, and other premium features. Check the Pricing page for full details.' },
  { category: 'billing',         q: 'How do I upgrade to Pro?',                              a: 'Go to the Pricing page, select the appropriate package, and follow the payment process. Your account will be upgraded instantly after successful payment.' },
  { category: 'billing',         q: 'Is there a refund if I\'m not satisfied?',               a: 'For refund inquiries, please open a support ticket on our Discord server (discord.gg/nshaJqNA4X). Our team will assist you within 1×24 business hours.' },
  { category: 'security',        q: 'Is my API Key securely stored on CatatCrypto?',         a: 'API Keys are stored encrypted. We only use them to read your trading history and never for any other action. As a best practice, use a read-only API Key without withdrawal permissions.' },
  { category: 'security',        q: 'Is my trading data shared with third parties?',         a: 'No. Your trading data is private and never shared with third parties for any purpose. Read our Privacy Policy for full details.' },
];

const FAQItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all duration-200 cursor-pointer select-none ${
        open ? 'border-[#E50914]/25 bg-[#E50914]/4' : 'border-neutral-900 bg-neutral-950/40 hover:border-neutral-800'
      }`}
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between px-5 py-4 gap-4">
        <span className={`text-[13px] font-bold leading-snug transition-colors ${open ? 'text-white' : 'text-neutral-300'}`}>
          {q}
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.18 }} className="shrink-0">
          <ChevronDown size={15} className={open ? 'text-[#E50914]' : 'text-neutral-600'} />
        </motion.div>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="px-5 pb-5 text-[13px] text-neutral-400 leading-relaxed border-t border-neutral-900 pt-3">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQContent: React.FC<{ formData?: any }> = ({ formData: _formData }) => {
  const [search, setSearch]           = useState('');
  const [activeCategory, setActive]   = useState('all');

  const filtered = faqs.filter(f => {
    const matchCat    = activeCategory === 'all' || f.category === activeCategory;
    const matchSearch = search === '' || f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="flex-1">
      {/* Hero header — mirrors DashboardPage / TradesPage header style */}
      <div className="px-8 py-10 border-b border-neutral-900 bg-[#080808] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[200px] rounded-full bg-[#E50914]/4 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E50914]/10 border border-[#E50914]/20 text-[9px] font-black uppercase tracking-widest text-[#E50914] mb-3">
            <HelpCircle size={9} />
            Support Center
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight mb-1.5">FAQ & Support</h1>
          <p className="text-xs text-neutral-500 leading-relaxed max-w-lg">
            Find quick answers about CatatCrypto. Nothing matches?
            Join our Discord and open a support ticket.
          </p>
        </div>
      </div>

      <div className="px-8 py-8 max-w-[900px] space-y-6">
        {/* Search + category row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Search */}
          <div className="relative w-full sm:w-64 shrink-0">
            <Search size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-600" />
            <input
              type="text"
              placeholder="Search questions..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-900 hover:border-neutral-800 focus:border-[#E50914]/40 focus:outline-none rounded-xl py-2 pl-9 pr-4 text-xs text-white placeholder-neutral-600 transition-colors"
            />
          </div>

          {/* Category chips */}
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setActive('all')}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all border ${
                activeCategory === 'all'
                  ? 'bg-[#E50914] border-[#E50914] text-white'
                  : 'bg-transparent border-neutral-900 text-neutral-500 hover:text-white hover:border-neutral-700'
              }`}
            >
              All
            </button>
            {faqCategories.map(cat => {
              const Icon   = cat.icon;
              const active = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActive(cat.id)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all border flex items-center gap-1 ${
                    active
                      ? `${cat.bg} ${cat.border} ${cat.color}`
                      : 'bg-transparent border-neutral-900 text-neutral-500 hover:text-white hover:border-neutral-700'
                  }`}
                >
                  <Icon size={10} />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* FAQ accordion list */}
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <HelpCircle size={32} className="text-neutral-800 mx-auto mb-3" />
            <p className="text-neutral-500 text-sm font-bold">No matching questions found.</p>
            <p className="text-neutral-600 text-xs mt-1">Try other keywords or ask directly on Discord.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((f, i) => (
              <React.Fragment key={i}>
                <FAQItem q={f.q} a={f.a} />
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Discord CTA — tight, matches the app's card style */}
        <div className="rounded-2xl border border-neutral-900 bg-neutral-950 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="w-10 h-10 rounded-xl bg-[#5865F2]/15 border border-[#5865F2]/25 flex items-center justify-center shrink-0">
            <MessageCircle size={18} className="text-[#5865F2]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black text-white mb-0.5">Still have questions?</p>
            <p className="text-xs text-neutral-500 leading-relaxed">
              Join the CatatCrypto Discord server and open a support ticket. Our team is active and ready to help every day.
            </p>
          </div>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] text-white font-black rounded-xl text-[11px] uppercase tracking-wider transition-all shrink-0 active:scale-95"
          >
            <MessageCircle size={13} />
            Join Discord
            <ExternalLink size={11} className="opacity-70" />
          </a>
        </div>

        <p className="text-[10px] text-neutral-700 pb-4">
          Temporary support center via Discord · Roadmap: helpdesk platform integration (Zendesk/Freshdesk)
        </p>
      </div>
    </div>
  );
};

import { useSEO } from '../hooks/useSEO';

const FAQPage: React.FC<FAQPageProps> = ({ formData }) => {
  useSEO({
    title: 'FAQ & Support Help Center',
    description: 'Temukan jawaban atas pertanyaan umum tentang CatatCrypto, keamanan koneksi API CEX, penagihan, dan sinkronisasi transaksi.',
    keywords: 'faq, support, help desk, api security, billing, exchange sync, catatcrypto support',
    canonical: 'https://catatcrypto.com/faq',
    ogUrl: 'https://catatcrypto.com/faq'
  });

  const isLoggedIn = !!localStorage.getItem('auth_token');

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#080808] text-white font-sans flex flex-col">
        <div className="flex items-center gap-2.5 px-8 py-4 border-b border-neutral-900 bg-black">
          <span className="text-white font-black text-sm tracking-tight">CatatCrypto</span>
          <span className="text-neutral-700 text-xs">/</span>
          <span className="text-neutral-500 text-xs font-bold uppercase tracking-wider">FAQ</span>
        </div>
        <FAQContent formData={formData} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex font-sans">
      <Sidebar plan={formData?.plan} />
      <main className="flex-1 bg-[#0a0a0a] min-h-screen flex flex-col">
        <TopBar name={formData?.name} title="FAQ & Support" />
        <FAQContent formData={formData} />
      </main>
    </div>
  );
};

export default FAQPage;
